// Dispatch-kø (DISP-01, B-003 «Dispatch = SKADE») — vaktsjefens delte anropskø.
//
// Eierbeslutning B-003: Zellos Dispatch Hub (egen skrivebordsapp, delt
// anropskø med prioritering og lastdeling mellom operatører) bygges IKKE som
// eget produkt. Motorene finnes allerede i COP: `analystQueue` gir poengsum
// OG begrunnelse, `precedence.ts` gir hastegraden. Dette panelet er derfor et
// PANEL OVER MOTORER VI HAR, ikke et nytt system — se ZELLO_MATRISE_2026-08-18
// (DISP-01) og BESLUTNINGER.md (B-003).
//
// ⚠ FORHOLDET TIL `ui/rfi.ts`: dette er IKKE et duplikat. RFI-panelet er
// ANALYTIKERENS arbeidsflate (opprett RFI, sett krav, tildel oppdrag, svar).
// Dette panelet er VAKTSJEFENS oversikt over SAMME kø, ordnet som Zellos
// dispatch-skjerm: gruppert på hastegrad (FLASH → ROUTINE), med poengsum +
// begrunnelse synlig per rad, og ÉN handling ingen annen flate har:
// klaim/frigjøring, så to operatører ikke jobber samme spørsmål dobbelt
// (Zellos «lastdeling mellom operatører»). Besvarte/lukkede RFI-er har ingen
// plass her — de er ferdigbehandlet, ikke noe å dispatche (samme filter som
// `analystQueue` selv bruker).
//
// Klaim er et FELT PÅ `Rfi` (`claimedBy`/`claimedAtMs`, se rfi.ts), som flyter
// gjennom SAMME sanntids-collab-kanal som resten av RFI-en (status/svar) —
// hubben validerer (zod) og STEMPLER klaimeren til autentisert avsender
// (`collab.ts`, anti-spoof §4) før den fanoutes org-scopet til alle andre
// operatører. Ekte, ikke en klient-lokal illusjon: to nettleservinduer som
// begge har dette panelet oppe ser klaimet forsvinne fra «ledig» samtidig.
import {
  analystQueue, precedenceOrder, PRECEDENCE_LABEL, PRECEDENCE_CODEWORD,
  RfiPriority, REQUIREMENT_LABEL, formatMgrs, claimRfi, releaseRfi,
  type Rfi, type RfiPriority as RfiPriorityId, type Requirement, type ScoredRfi,
} from '@skade/contracts';

// Samme fire kodeord-farger som `ui/rfi.ts` — MED VILJE identisk, ikke bare
// tilfeldig likt: samme hastegrad skal se lik ut uansett hvilket panel
// operatøren har åpent. (Ikke sentralisert i contracts ennå — begge stedene
// må endres sammen om fargene en dag flyttes dit.)
const PRIO_COLOR: Record<RfiPriorityId, string> = {
  flash: '#ff3b3b', immediate: '#ff9a3b', priority: '#ffd24a', routine: '#8fb0c8',
};
const STATUS_LABEL: Record<Rfi['status'], string> = {
  open: 'Åpen', tasked: 'Tildelt', answered: 'Besvart', closed: 'Lukket',
};
const TIERS: RfiPriorityId[] = precedenceOrder(RfiPriority.options);

export interface DispatchHooks {
  self: string;
  getRfis: () => Rfi[];
  /** Kringkast en oppdatert RFI over hubben (klaim/frigjøring er bare et nytt
   * feltsett på samme RFI — samme kanal som resten av RFI-livssyklusen). */
  publish: (r: Rfi) => void;
  /** Tida sendes inn; panelet leser ikke klokka selv. */
  naa: () => number;
  /** Etterretningskravene triagen vekter mot — se `RfiHooks.getRequirements`
   * i `ui/rfi.ts` for hvorfor (samme motor, samme grunn). */
  getRequirements?: () => Requirement[];
}

export class DispatchPanel {
  readonly el: HTMLElement;
  private open = false;
  private listEl!: HTMLElement;

  constructor(private hooks: DispatchHooks) {
    this.el = this.build();
    this.render();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) this.render();
  }

  private build(): HTMLElement {
    const p = div('report-composer dispatch-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Dispatch-kø'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);
    this.listEl = div('targetlist-list');
    p.append(this.listEl);
    return p;
  }

  render() {
    const alle = this.hooks.getRfis();
    const now = this.hooks.naa();
    const krav = this.hooks.getRequirements?.() ?? [];
    // `analystQueue` filtrerer selv til åpne/tildelte og sorterer på poengsum
    // (høyest først). Grupperingen under BEVARER den rekkefølgen innen hver
    // hastegrad-bøtte — filter() endrer aldri innbyrdes rekkefølge.
    const kø = analystQueue(alle, now, krav);

    this.listEl.innerHTML = '';
    if (!kø.length) { this.listEl.append(span('checklist-note', 'Ingen RFI-er i kø.')); return; }

    for (const tier of TIERS) {
      const iTier = kø.filter((k) => k.rfi.priority === tier);
      if (!iTier.length) continue;
      const head = div('dispatch-tier-head');
      head.append(
        span('dispatch-tier-label', PRECEDENCE_LABEL[tier]),
        span('dispatch-tier-count', String(iTier.length)),
      );
      this.listEl.append(head);
      for (const scored of iTier) this.listEl.append(this.row(scored, krav, now));
    }
  }

  private row(scored: ScoredRfi, krav: Requirement[], now: number): HTMLElement {
    const r = scored.rfi;
    const row = div('targetlist-row');
    const dot = document.createElement('span');
    dot.className = 'checklist-dot'; dot.style.background = PRIO_COLOR[r.priority];
    dot.title = `Hastegrad: ${PRECEDENCE_LABEL[r.priority]}`;

    const main = div('checklist-main');
    const tittel = div('checklist-item-title');
    // Poengsummen VISES, med begrunnelsen — samme regel som RFI-panelet
    // (§1: en rangering operatøren ikke kan etterprøve er en hun ikke bør
    // stole på). Gull = «se hit», systemets ene aksent (UX §9b).
    const p = span('rfi-poeng', String(scored.score));
    p.title = scored.reasons.length
      ? `Triage ${scored.score}/100 — ${scored.reasons.join(', ')}`
      : `Triage ${scored.score}/100`;
    tittel.append(p, span('checklist-item-title-tekst', r.question));
    main.append(tittel);

    const overdue = r.dueMs !== undefined && r.dueMs < now;
    const sub = span(
      'checklist-note',
      `${r.requester} · ${PRECEDENCE_CODEWORD[r.priority]} · ${STATUS_LABEL[r.status]}`
      + `${r.mgrs ? ` · ${formatMgrs(r.mgrs)}` : ''}${overdue ? ' · FORFALT' : ''}`,
    );
    if (overdue) sub.style.color = '#ff8a8a';
    main.append(sub);

    if (scored.reasons.length) {
      const kravTekst = r.requirementId ? krav.find((k) => k.id === r.requirementId) : undefined;
      const deler = [...scored.reasons];
      if (kravTekst) deler.push(`${REQUIREMENT_LABEL[kravTekst.kind]}: ${kravTekst.text.slice(0, 60)}`);
      main.append(span('rfi-begrunnelse', `Triage: ${deler.join(' · ')}`));
    }

    // ── Lastdeling (DISP-01): klaim/frigjøring ────────────────────────────
    // Ledig → «Ta». Klaimet av MEG → vis det + «Frigi». Klaimet av EN ANNEN →
    // vis hvem, INGEN knapp — det er selve poenget: en operatør som ser «tatt
    // av OSO-4» starter ikke samme jobb på nytt (§1: ærlig, ikke skjult/fjernet
    // fra visning — begge operatører skal se hele køen og hvem som gjør hva).
    const actions = div('targetlist-actions');
    if (r.claimedBy && r.claimedBy !== this.hooks.self) {
      actions.append(span('dispatch-tatt', `Tatt av ${r.claimedBy}`));
    } else if (r.claimedBy === this.hooks.self) {
      actions.append(span('dispatch-tatt dispatch-tatt-meg', 'Du har denne'));
      actions.append(btn('Frigi', () => this.frigi(r)));
    } else {
      actions.append(btn('Ta', () => this.klaim(r)));
    }

    row.append(dot, main, actions);
    return row;
  }

  private klaim(r: Rfi) {
    this.hooks.publish(claimRfi(r, this.hooks.self, this.hooks.naa()));
    this.render();
  }

  private frigi(r: Rfi) {
    this.hooks.publish(releaseRfi(r, this.hooks.naa()));
    this.render();
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function btn(text: string, run: () => void): HTMLButtonElement {
  const b = document.createElement('button'); b.className = 'targetlist-btn'; b.textContent = text; b.onclick = run; return b;
}
